import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Bell, BellSlash, CheckCircle, WarningCircle, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * PushNotificationSection — Fase 12A (Web Push mínimo).
 * Sección en /dashboard/cuenta que permite al asesor activar/desactivar Web Push.
 * NUNCA solicita permiso automáticamente: solo tras clic explícito en "Activar".
 * En iOS <16.4 o sin PWA instalada, muestra un aviso claro con instrucciones.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function PushNotificationSection() {
  const [supported, setSupported] = useState(true);
  const [installed, setInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [iosVersion, setIosVersion] = useState(null);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const has = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(has);
    const ua = navigator.userAgent || "";
    const ios = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    setIsIOS(ios);
    if (ios) {
      const m = ua.match(/OS (\d+)_(\d+)/);
      if (m) setIosVersion(parseFloat(`${m[1]}.${m[2]}`));
    }
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    setInstalled(standalone);
    if (has) setPermission(Notification.permission);
    // Check current subscription
    if (has && navigator.serviceWorker?.getRegistration) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (!reg) return;
        reg.pushManager.getSubscription().then((sub) => {
          setSubscribed(!!sub);
        }).catch(() => {});
      }).catch(() => {});
    }
  }, []);

  const canActivate = supported && (!isIOS || (isIOS && iosVersion >= 16.4 && installed));
  const iosBlockedReason = isIOS && (iosVersion == null || iosVersion < 16.4)
    ? "Actualiza tu iPhone a iOS 16.4 o superior para recibir notificaciones."
    : (isIOS && !installed)
    ? "Agrega ROCAFUERTE a tu pantalla de inicio para activar las notificaciones."
    : null;

  const activate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Register SW if not already
      let reg = await navigator.serviceWorker.getRegistration();
      if (!reg) reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // Explicit user-gesture permission request (required by iOS)
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Permiso denegado. Puedes activarlo desde los ajustes del navegador.");
        return;
      }
      // Fetch VAPID public key
      const { data: vapid } = await api.get("/push/vapid-public");
      if (!vapid?.public_key) throw new Error("no-vapid");
      const appServerKey = urlBase64ToUint8Array(vapid.public_key);
      // Subscribe
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appServerKey });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success("Notificaciones activadas. Te avisaremos de nuevos prospectos.");
    } catch (err) {
      // Never expose provider/stack — friendly neutral message.
      toast.error("No pudimos activar las notificaciones. Vuelve a intentar más tarde.");
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint, keys: sub.toJSON().keys }); } catch { /* noop */ }
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notificaciones desactivadas en este dispositivo.");
    } catch {
      toast.error("No pudimos desactivar las notificaciones.");
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-5 sm:p-6" data-testid="push-notifications-section">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[#C8A24A]/10 border border-[#C8A24A]/25 flex items-center justify-center flex-shrink-0">
          <Bell size={20} weight="duotone" className="text-[#C8A24A]" />
        </div>
        <div className="min-w-0">
          <h2 className="font-heading text-xl tracking-tight" data-testid="push-notifications-title">
            Notificaciones de nuevos prospectos
          </h2>
          <p className="text-xs text-[#C8A24A]/70 mt-1 leading-relaxed">
            Recibe un aviso cuando alguien pregunte por una de tus propiedades, incluso cuando la app esté cerrada.
          </p>
        </div>
      </div>

      {!supported && (
        <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-100 flex items-start gap-2" data-testid="push-not-supported">
          <WarningCircle size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
          <span>Este navegador no soporta notificaciones. Usa Chrome (Android) o Safari (iPhone).</span>
        </div>
      )}

      {supported && iosBlockedReason && (
        <div className="mt-4 rounded-lg border border-[#C8A24A]/25 bg-[#C8A24A]/5 p-3 text-xs text-[#C8A24A] flex items-start gap-2" data-testid="push-ios-blocked">
          <Info size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
          <span data-testid="push-ios-blocked-msg">{iosBlockedReason}</span>
        </div>
      )}

      {supported && !iosBlockedReason && (
        <div className="mt-4">
          {permission === "denied" ? (
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-100 flex items-start gap-2" data-testid="push-permission-denied">
              <WarningCircle size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
              <span>Permiso denegado. Actívalo desde los ajustes del navegador y regresa aquí.</span>
            </div>
          ) : subscribed ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-100 flex items-start gap-2" data-testid="push-active">
                <CheckCircle size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
                <span>Notificaciones activadas en este dispositivo.</span>
              </div>
              <Button
                onClick={deactivate}
                variant="outline"
                disabled={busy}
                className="h-10"
                data-testid="push-deactivate-btn"
              >
                <BellSlash size={14} className="mr-1.5" /> Desactivar en este dispositivo
              </Button>
            </div>
          ) : (
            <Button
              onClick={activate}
              disabled={busy || !canActivate}
              className="h-11 bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111]"
              data-testid="push-activate-btn"
            >
              <Bell size={16} weight="fill" className="mr-1.5" />
              {busy ? "Activando..." : "Activar notificaciones"}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
